import React, { useState, useEffect } from 'react';
import axios from 'axios';
import IncomeForm from './IncomeForm';
import './IncomeDashboard.css';
import { API_BASE_URL } from '../lib/api';

function IncomeDashboard() {
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [filter, setFilter] = useState('all'); // all, dividends, rental, gifts, etc.

  useEffect(() => {
    fetchIncome();
    fetchSummary();
  }, []);

  const fetchIncome = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/income`, {
        withCredentials: true
      });
      setIncomeEntries(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching income:', err);
      setError('Failed to load income entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/income/summary`, {
        withCredentials: true
      });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleDelete = async (incomeId) => {
    if (!window.confirm('Are you sure you want to delete this income entry?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/income/${incomeId}`, {
        withCredentials: true
      });
      fetchIncome();
      fetchSummary();
    } catch (err) {
      console.error('Error deleting income:', err);
      alert('Failed to delete income entry');
    }
  };

  const handleEdit = (income) => {
    setEditingIncome(income);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingIncome(null);
    fetchIncome();
    fetchSummary();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingIncome(null);
  };

  const filteredIncome = filter === 'all'
    ? incomeEntries
    : incomeEntries.filter(i => i.category.toLowerCase().replace(' ', '') === filter.toLowerCase());

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getFrequencyBadge = (frequency) => {
    const badges = {
      monthly: { label: 'Monthly', class: 'badge-monthly' },
      yearly: { label: 'Yearly', class: 'badge-yearly' },
      quarterly: { label: 'Quarterly', class: 'badge-quarterly' },
      'one-time': { label: 'One-time', class: 'badge-onetime' }
    };
    const badge = badges[frequency] || { label: frequency, class: '' };
    return <span className={`frequency-badge ${badge.class}`}>{badge.label}</span>;
  };

  if (loading && incomeEntries.length === 0) {
    return (
      <div className="income-dashboard">
        <div className="loading">Loading income entries...</div>
      </div>
    );
  }

  return (
    <div className="income-dashboard">
      <div className="income-header">
        <h2>💰 Income Tracker</h2>
        <button className="btn-add" onClick={() => setShowForm(true)}>
          + Add Income
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Summary Cards */}
      {summary && (
        <div className="income-summary">
          <div className="summary-card income-card">
            <div className="summary-label">Monthly Income</div>
            <div className="summary-value green">{formatCurrency(summary.monthlyTotal)}</div>
            <div className="summary-subtitle">{summary.recurringCount} recurring sources</div>
          </div>
          <div className="summary-card income-card">
            <div className="summary-label">Yearly Income</div>
            <div className="summary-value green">{formatCurrency(summary.yearlyTotal)}</div>
            <div className="summary-subtitle">Projected annual</div>
          </div>
          <div className="summary-card income-card">
            <div className="summary-label">Total Entries</div>
            <div className="summary-value">{summary.totalEntries}</div>
            <div className="summary-subtitle">{summary.oneTimeCount} one-time</div>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && summary.breakdown && Object.keys(summary.breakdown).length > 0 && (
        <div className="category-breakdown">
          <h3>📊 Breakdown by Category</h3>
          <div className="breakdown-grid">
            {Object.entries(summary.breakdown)
              .sort((a, b) => b[1].monthlyTotal - a[1].monthlyTotal)
              .map(([category, data]) => (
                <div key={category} className="breakdown-card income-breakdown-card">
                  <div className="breakdown-category">{category}</div>
                  <div className="breakdown-count">{data.count} {data.count === 1 ? 'source' : 'sources'}</div>
                  <div className="breakdown-monthly income-amount">{formatCurrency(data.monthlyTotal)}/mo</div>
                  <div className="breakdown-yearly income-amount">{formatCurrency(data.yearlyTotal)}/yr</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="income-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({incomeEntries.length})
        </button>
        {['Dividends', 'Rental Income', 'Gifts', 'Investment Returns', 'Other'].map(cat => {
          const count = incomeEntries.filter(i => i.category === cat).length;
          return count > 0 ? (
            <button
              key={cat}
              className={filter === cat.toLowerCase().replace(' ', '') ? 'active' : ''}
              onClick={() => setFilter(cat.toLowerCase().replace(' ', ''))}
            >
              {cat} ({count})
            </button>
          ) : null;
        })}
      </div>

      {/* Income List */}
      {filteredIncome.length === 0 ? (
        <div className="empty-state">
          <p>No income entries found. Add your first income source to get started!</p>
        </div>
      ) : (
        <div className="income-table-container">
          <table className="income-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Frequency</th>
                <th>Monthly Income</th>
                <th>Date Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncome.map(income => {
                // Calculate monthly income
                let monthlyIncome = income.amount;
                if (income.frequency === 'yearly') monthlyIncome = income.amount / 12;
                if (income.frequency === 'quarterly') monthlyIncome = income.amount / 3;
                if (income.frequency === 'one-time') monthlyIncome = 0;

                return (
                  <tr key={income.incomeId}>
                    <td className="income-name">
                      {income.name}
                      {income.notes && (
                        <span className="income-notes" title={income.notes}>
                          📝
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="category-badge income-category-badge">{income.category}</span>
                    </td>
                    <td className="amount income-amount">
                      {formatCurrency(income.amount, income.currency)}
                    </td>
                    <td>{getFrequencyBadge(income.frequency)}</td>
                    <td className="monthly-income income-amount">
                      {monthlyIncome > 0 ? formatCurrency(monthlyIncome, income.currency) : '-'}
                    </td>
                    <td>{new Date(income.date).toLocaleDateString()}</td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(income)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(income.incomeId)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <IncomeForm
          income={editingIncome}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
}

export default IncomeDashboard;
